import OpenAI from 'openai';
import { supabase } from '../lib/supabase';

/**
 * Travel Policy Assistant Service
 * AI-powered assistant for travel policy recommendations, clarifications, and risk assessments
 */

class TravelPolicyAssistantService {
  constructor() {
    this.openai = new OpenAI({
      apiKey: import.meta.env.VITE_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true,
    });
  }

  /**
   * Get AI-powered travel policy recommendations
   */
  async getChatCompletion(userMessage, conversationHistory = []) {
    try {
      // Fetch travel policies from database
      const policies = await this._getTravelPolicies();
      const policyContext = this._formatPoliciesForContext(policies);

      // Build conversation messages
      const messages = [
        {
          role: 'system',
          content: `You are an expert travel policy assistant for RoamSure LLC. Your role is to provide accurate, helpful guidance on travel policies, risk assessments, and compliance requirements.

**Available Travel Policies:**
${policyContext}

**Your Responsibilities:**
1. Answer questions about travel policies clearly and concisely
2. Provide risk assessments for travel requests based on destination, type, and budget
3. Clarify compliance requirements and approval workflows
4. Suggest cost-saving alternatives when appropriate
5. Flag potential policy violations and explain how to resolve them
6. Recommend emergency procedures for urgent travel situations

**Response Guidelines:**
- Be professional yet friendly
- Cite specific policies when relevant
- Provide actionable recommendations
- Highlight compliance risks clearly
- Suggest next steps for the user

Always prioritize traveler safety and policy compliance.`,
        },
        ...conversationHistory,
        { role: 'user', content: userMessage },
      ];

      const response = await this.openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages,
        max_tokens: 1000,
        temperature: 0.7,
      });

      return {
        message: response?.choices?.[0]?.message?.content,
        role: 'assistant',
      };
    } catch (error) {
      console.error('Error in chat completion:', error);
      throw new Error('Failed to get AI response. Please try again.');
    }
  }

  /**
   * Analyze travel request for policy compliance and risks
   */
  async analyzeTravelRequest(travelRequest) {
    try {
      const policies = await this._getTravelPolicies();
      const policyContext = this._formatPoliciesForContext(policies);

      const analysisPrompt = `Analyze this travel request for policy compliance and risks:

**Travel Request Details:**
- Destination: ${travelRequest?.destination}
- Travel Type: ${travelRequest?.travelType}
- Duration: ${travelRequest?.startDate} to ${travelRequest?.endDate}
- Estimated Budget: $${travelRequest?.estimatedBudget}
- Purpose: ${travelRequest?.purpose}
- Priority: ${travelRequest?.priority}

**Available Policies:**
${policyContext}

Provide a structured analysis with:
1. Compliance Status (compliant/violation/needs review)
2. Risk Assessment (low/medium/high)
3. Key Concerns (if any)
4. Recommendations
5. Required Approvals`;

      const response = await this.openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: 'You are a travel policy compliance analyst. Provide structured, actionable analysis.',
          },
          { role: 'user', content: analysisPrompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'travel_request_analysis',
            schema: {
              type: 'object',
              properties: {
                complianceStatus: {
                  type: 'string',
                  enum: ['compliant', 'violation', 'needs_review'],
                },
                riskLevel: {
                  type: 'string',
                  enum: ['low', 'medium', 'high'],
                },
                concerns: {
                  type: 'array',
                  items: { type: 'string' },
                },
                recommendations: {
                  type: 'array',
                  items: { type: 'string' },
                },
                requiredApprovals: {
                  type: 'array',
                  items: { type: 'string' },
                },
                summary: { type: 'string' },
              },
              required: [
                'complianceStatus',
                'riskLevel',
                'concerns',
                'recommendations',
                'requiredApprovals',
                'summary',
              ],
              additionalProperties: false,
            },
          },
        },
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error analyzing travel request:', error);
      throw new Error('Failed to analyze travel request. Please try again.');
    }
  }

  /**
   * Get policy clarification for specific question
   */
  async getPolicyClarification(question) {
    try {
      const policies = await this._getTravelPolicies();
      const policyContext = this._formatPoliciesForContext(policies);

      const response = await this.openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are a travel policy expert. Answer questions about RoamSure LLC travel policies clearly and accurately.\n\n**Available Policies:**\n${policyContext}`,
          },
          { role: 'user', content: question },
        ],
        max_tokens: 500,
        temperature: 0.5,
      });

      return response?.choices?.[0]?.message?.content;
    } catch (error) {
      console.error('Error getting policy clarification:', error);
      throw new Error('Failed to get policy clarification. Please try again.');
    }
  }

  /**
   * Get quick recommendations for common scenarios
   */
  async getQuickRecommendation(scenario) {
    try {
      const scenarioPrompts = {
        emergency: 'What are the procedures and requirements for emergency travel requests?',
        international: 'What are the key requirements and considerations for international travel?',
        budget: 'What are the budget guidelines and approval thresholds for travel requests?',
        compliance: 'What are the main compliance requirements for travel requests?',
      };

      const prompt = scenarioPrompts?.[scenario] || scenario;
      return await this.getPolicyClarification(prompt);
    } catch (error) {
      console.error('Error getting quick recommendation:', error);
      throw new Error('Failed to get recommendation. Please try again.');
    }
  }

  /**
   * Fetch travel policies from database
   */
  async _getTravelPolicies() {
    try {
      const { data, error } = await supabase?.from('travel_policies')?.select('*')?.eq('is_active', true);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching travel policies:', error);
      return [];
    }
  }

  /**
   * Format policies for AI context
   */
  _formatPoliciesForContext(policies) {
    if (!policies || policies?.length === 0) {
      return 'No active travel policies found. Use general best practices for corporate travel.';
    }

    return policies?.map((policy, index) => {
        const rules = typeof policy?.rules === 'string' ? policy?.rules : JSON.stringify(policy?.rules, null, 2);
        return `${index + 1}. **${policy?.policy_name}** (${policy?.policy_type})\n   Description: ${policy?.description || 'N/A'}\n   Rules: ${rules}`;
      })?.join('\n\n');
  }

  /**
   * Convert snake_case to camelCase
   */
  _convertToCamelCase(data) {
    if (Array.isArray(data)) {
      return data?.map((item) => this._convertToCamelCase(item));
    }

    if (data !== null && typeof data === 'object') {
      return Object.keys(data)?.reduce((acc, key) => {
        const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
        acc[camelKey] = this._convertToCamelCase(data?.[key]);
        return acc;
      }, {});
    }

    return data;
  }
}

export default new TravelPolicyAssistantService();
